<script>
  import { onMount } from 'svelte';
  import { api } from '../lib/api.js';
  import PickList from '../components/PickList.svelte';
  import AddressPanel from '../components/AddressPanel.svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import EcardViews from '../components/EcardViews.svelte';
  import CardPageLink from '../components/CardPageLink.svelte';
  import { ecardBase } from '../lib/stores.js';

  export let params = {};

  let order = null;
  let loading = true;
  let error = null;

  let fulfilling = false;
  let trackingNumber = '';
  let actionMsg = null;

  onMount(load);

  async function load() {
    loading = true;
    error = null;
    try {
      const d = await api(`/orders/${params.id}`);
      order = d.order;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  async function fulfill() {
    if (!confirm('Mark this order as fulfilled in Shopify?')) return;
    fulfilling = true;
    actionMsg = null;
    try {
      const r = await api(`/orders/${params.id}/fulfill`, {
        method: 'POST',
        body: JSON.stringify({ trackingNumber: trackingNumber || undefined }),
      });
      order.state = r.state;
      order.fulfillmentStatus = 'FULFILLED';
      order = order;
      actionMsg = 'Order marked fulfilled.';
    } catch (e) {
      actionMsg = 'Error: ' + e.message;
    } finally {
      fulfilling = false;
    }
  }

  function money(m) {
    return m ? `${m.amount} ${m.currencyCode}` : '—';
  }

  const statusClass = (s) => ({
    sent: 'ok', paid: 'info', pending: 'wait', failed: 'bad', bounced: 'bad',
    delivered: 'ok', accepted: 'info', sending: 'wait', queued: 'wait', undelivered: 'bad',
  }[s] || 'wait');

  const recipientContact = (e) =>
    e.delivery === 'sms' ? e.recipientPhone : e.delivery === 'email' ? e.recipientEmail : null;

  $: isFulfilled =
    (order && order.state && order.state.fulfillment === 'fulfilled') ||
    (order && order.fulfillmentStatus === 'FULFILLED');
  $: hasAddress = !!(order && order.shippingAddress);
</script>

<a class="back" href="#/">← All orders</a>

{#if loading}
  <p class="muted">Loading order…</p>
{:else if error}
  <p class="error">⚠️ {error}</p>
{:else if order}
  <header class="oh">
    <h2>{order.name}</h2>
    <StatusBadge status={order.fulfillmentStatus} />
  </header>

  <div class="cols">
    <section class="pick">
      <h3>Items to pick</h3>
      <PickList items={order.lineItems} />
    </section>

    <aside class="side">
      <div class="card actions">
        <h4>Actions</h4>
        {#if hasAddress}
          <a class="btn ghost" href={`#/label/${order.orderNumber}`}>🏷️ Print shipping label</a>
          <a class="btn ghost" href={`#/packing-slip/${order.orderNumber}`}>📄 Print packing slip</a>
        {:else}
          <span class="btn ghost disabled" title="No shipping address">🏷️ No address to label</span>
        {/if}

        {#if isFulfilled}
          <p class="done">✅ Fulfilled{#if order.state && order.state.trackingNumber} · {order.state.trackingNumber}{/if}</p>
        {:else}
          <input class="track" placeholder="Tracking № (optional)" bind:value={trackingNumber} />
          <button class="btn solid" on:click={fulfill} disabled={fulfilling}>
            {fulfilling ? 'Fulfilling…' : 'Mark as fulfilled'}
          </button>
        {/if}
        {#if actionMsg}<p class="msg">{actionMsg}</p>{/if}
      </div>

      {#if order.ecards && order.ecards.length}
        <div class="card ecard">
          <h4>🎴 Ecard{order.ecards.length > 1 ? `s · ${order.ecards.length}` : ''}</h4>
          {#each order.ecards as e (e.id)}
            <div class="ec">
              <div class="ec-top">
                <span class="title">{e.productTitle || e.productId || 'Ecard'}</span>
                <span class="badge {statusClass(e.status)}">{e.status || '—'}</span>
                {#if e.delivery === 'sms' && e.smsStatus}<span class="badge sm {statusClass(e.smsStatus)}">{e.smsStatus}</span>{/if}
              </div>
              <dl>
                {#if e.audioLabel}<dt>Sound</dt><dd>♪ {e.audioLabel}</dd>{/if}
                <dt>Delivery</dt><dd>{e.delivery || '—'}</dd>
                <dt>Recipient</dt><dd>{e.recipientName || '—'}{#if recipientContact(e)} · {recipientContact(e)}{/if}</dd>
                <dt>Send</dt><dd>{e.sendDate || '—'}{#if e.sendTime} at {e.sendTime}{/if}</dd>
                <dt>Views</dt><dd>{e.viewCount || 0}</dd>
              </dl>
              {#if e.message}<p class="msg">“{e.message}”</p>{/if}
              <div class="ec-actions">
                <EcardViews ecardId={e.id} viewCount={e.viewCount || 0} />
                {#if e.shortId}<a class="view-link" href={`${$ecardBase}/ecard/${e.id}?crdv=a`} target="_blank" rel="noopener">Preview ecard ↗</a>{/if}
              </div>
              <div class="ec-actions">
                <CardPageLink ecard={e} customerEmail={order.customer && order.customer.email} customerFirstName={order.customer && order.customer.firstName} />
              </div>
            </div>
          {/each}
        </div>
      {/if}

      <AddressPanel customer={order.customer} address={order.shippingAddress} shippingMethod={order.shippingMethod} />

      {#if order.note}
        <div class="card note">
          <h4>Order note</h4>
          <p>{order.note}</p>
        </div>
      {/if}

      <div class="card totals">
        <div><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
        <div><span>Shipping</span><span>{money(order.shipping)}</span></div>
        <div class="grand"><span>Total</span><span>{money(order.total)}</span></div>
        <div><span>Weight</span><span>{order.weightGrams ? order.weightGrams + ' g' : '—'}</span></div>
      </div>
    </aside>
  </div>
{/if}

<style>
  .back { display: inline-block; margin-bottom: .75rem; color: #1d4ed8; text-decoration: none; font-size: .9rem; }
  .oh { display: flex; align-items: center; gap: .75rem; }
  .oh h2 { margin: 0; }
  .cols { display: grid; grid-template-columns: 1fr 320px; gap: 1.5rem; margin-top: 1rem; align-items: start; }
  .pick { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 1rem 1.25rem; }
  .pick h3 { margin-top: 0; }
  .side { display: flex; flex-direction: column; gap: 1rem; }
  .card { background: #fff; border: 1px solid #eee; border-radius: 8px; padding: 1rem; }
  .card h4 { margin: 0 0 .5rem; font-size: .8rem; text-transform: uppercase; letter-spacing: .04em; color: #888; }
  .actions { display: flex; flex-direction: column; gap: .6rem; }
  .btn { display: inline-block; text-align: center; padding: .55rem .8rem; border-radius: 6px; cursor: pointer; font-size: .9rem; text-decoration: none; border: 1px solid transparent; }
  .btn.ghost { background: #fff; border-color: #ccc; color: #111; }
  .btn.solid { background: #111; color: #fff; border: 0; }
  .btn.disabled { opacity: .5; cursor: default; }
  .track { padding: .5rem; border: 1px solid #ccc; border-radius: 6px; font-size: .85rem; }
  .done { color: #166534; font-weight: 600; margin: .2rem 0 0; }
  .msg { font-size: .85rem; color: #555; margin: .2rem 0 0; }
  .totals div { display: flex; justify-content: space-between; padding: .2rem 0; font-size: .9rem; }
  .totals .grand { font-weight: 700; border-top: 1px solid #eee; margin-top: .25rem; padding-top: .4rem; }
  .muted { color: #777; }
  .error { color: #b00020; }
  .ecard { border-color: #e9d5ff; background: #faf5ff; }
  .ec + .ec { border-top: 1px solid #e9d5ff; margin-top: .6rem; padding-top: .6rem; }
  .ec-top { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; margin-bottom: .4rem; }
  .ec-top .title { font-weight: 600; font-size: .9rem; }
  .ecard dl { display: grid; grid-template-columns: auto 1fr; gap: .15rem .6rem; margin: 0; font-size: .85rem; }
  .ecard dt { color: #888; }
  .ecard dd { margin: 0; }
  .ecard .msg { font-style: italic; color: #555; font-size: .85rem; margin: .5rem 0 .3rem; }
  .ec-actions { display: flex; align-items: baseline; gap: .9rem; flex-wrap: wrap; margin-top: .4rem; }
  .view-link { font-size: .82rem; color: #6b21a8; text-decoration: none; font-weight: 600; }
  .badge { display: inline-block; border-radius: 6px; padding: .05rem .4rem; font-size: .72rem; font-weight: 600; text-transform: capitalize; }
  .badge.sm { font-weight: 500; }
  .badge.ok { background: #dcfce7; color: #166534; }
  .badge.info { background: #dbeafe; color: #1e40af; }
  .badge.wait { background: #fef9c3; color: #854d0e; }
  .badge.bad { background: #fee2e2; color: #991b1b; }
  @media (max-width: 760px) { .cols { grid-template-columns: 1fr; } }
</style>
